import React, {createContext, useContext, useMemo} from 'react';
import type {Dispatch} from 'react';

import type {EditorCanvasTypes} from '@edume/magnificent';

import {actionTypes} from './reducerState';

type AdminEditorCanvasObject =
  | Extract<EditorCanvasTypes.BaseObject, {type: 'text'}>
  | Extract<EditorCanvasTypes.BaseObject, {type: 'emoji'}>;

type SlideCanvasActionsContextType = {
  dispatch: Dispatch<Action>;
  onCanvasObjectMove: (object: AdminEditorCanvasObject) => void;
  onCanvasObjectRemove: (object: AdminEditorCanvasObject) => void;
  onCanvasObjectReorder: (object: AdminEditorCanvasObject) => void;
};

const SlideCanvasActionsContext =
  createContext<SlideCanvasActionsContextType | null>(null);

type Action = {
  type:
    | typeof actionTypes.MOVE_OVERLAY_ELEMENT
    | typeof actionTypes.REMOVE_OVERLAY_ELEMENT
    | typeof actionTypes.REORDER_OVERLAY_ELEMENT;
  payload: AdminEditorCanvasObject;
};

export const SlideCanvasActionsProvider = ({
  children,
  dispatch,
}: {
  children: React.ReactNode;
  dispatch: Dispatch<Action>;
}) => {
  const handlers = useMemo(
    () => ({
      dispatch,
      onCanvasObjectMove: (object: AdminEditorCanvasObject) => {
        dispatch({
          type: actionTypes.MOVE_OVERLAY_ELEMENT,
          payload: {...object},
        });
      },
      onCanvasObjectRemove: (object: AdminEditorCanvasObject) => {
        dispatch({
          type: actionTypes.REMOVE_OVERLAY_ELEMENT,
          payload: {...object},
        });
      },
      onCanvasObjectReorder: (object: AdminEditorCanvasObject) => {
        dispatch({
          type: actionTypes.REORDER_OVERLAY_ELEMENT,
          payload: {...object},
        });
      },
    }),
    [dispatch]
  );

  return (
    <SlideCanvasActionsContext.Provider value={handlers}>
      {children}
    </SlideCanvasActionsContext.Provider>
  );
};

export const useSlideCanvasActions = () => {
  const context = useContext(SlideCanvasActionsContext);
  if (context === null) {
    throw new Error(
      'useSlideCanvasActions must be used within a SlideCanvasActionsProvider'
    );
  }
  return context;
};
